import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Receipt, Plus, RefreshCw } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "../../components/ui/dialog";
import { invoicesApi, formatApiError } from "../../lib/ledgerApi";
import { useAuth } from "../../contexts/AuthContext";
import { toast } from "sonner";

const CURRENCIES = ["USD", "EUR", "GBP", "INR", "JPY", "AED", "NEO"];

const STATUS_PILL = {
  issued: "border-status-info/40 bg-status-info/10 text-status-info",
  paid: "border-status-ok/40 bg-status-ok/10 text-status-ok",
  cancelled: "border-border bg-muted text-muted-foreground",
  overdue: "border-status-tampered/40 bg-status-tampered/10 text-status-tampered",
};

const fmt = (s) => {
  try {
    const n = Number(s);
    return Number.isFinite(n) ? n.toLocaleString(undefined, { maximumFractionDigits: 6 }) : s;
  } catch { return s; }
};

export default function Invoices() {
  const { business } = useAuth();
  const [tab, setTab] = useState("any");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  // Form state
  const [payerVpa, setPayerVpa] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [amount, setAmount] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [memo, setMemo] = useState("");

  const load = async () => {
    try {
      const r = await invoicesApi.list({ role: tab });
      setItems(r);
    } catch (e) {
      toast.error("Load failed", { description: formatApiError(e.response?.data?.detail) });
    }
  };

  useEffect(() => {
    load();
    const i = setInterval(load, 5000);
    return () => clearInterval(i);
  }, [tab]); // eslint-disable-line

  const onCreate = async () => {
    if (!payerVpa || !amount) return;
    setBusy(true);
    try {
      await invoicesApi.create({
        payer_vpa: payerVpa, currency, amount,
        due_date: dueDate ? new Date(dueDate).toISOString() : null,
        memo: memo || null, line_items: [],
      });
      toast.success("Invoice issued");
      setOpen(false);
      setPayerVpa(""); setAmount(""); setDueDate(""); setMemo("");
      await load();
    } catch (e) {
      toast.error("Issue failed", { description: formatApiError(e.response?.data?.detail) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-7xl px-6 md:px-10 py-10 md:py-14" data-testid="invoices-page">
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-3">/ Invoices</div>
          <h1 className="text-3xl sm:text-4xl tracking-tight font-medium flex items-center gap-3">
            <Receipt className="w-6 h-6 text-muted-foreground" strokeWidth={1.5} /> Invoices
          </h1>
          <p className="mt-3 text-sm text-muted-foreground">
            Issue a request for payment. Approving an invoice auto-creates an invoice-linked contract on the payer side and settles immediately.
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button data-testid="invoices-new-btn" className="inline-flex items-center gap-2 px-4 py-2 rounded-sm border border-foreground bg-foreground text-background text-sm hover:opacity-90">
              <Plus className="w-4 h-4" strokeWidth={1.5} /> New invoice
            </button>
          </DialogTrigger>
          <DialogContent className="rounded-sm">
            <DialogHeader>
              <DialogTitle>Issue invoice</DialogTitle>
              <DialogDescription>Send a payment request to another business.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Payer VPA</Label>
                <Input data-testid="inv-payer-input" value={payerVpa} onChange={(e) => setPayerVpa(e.target.value)} placeholder="acme@neopay" className="rounded-sm font-mono mt-1" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">Currency</Label>
                  <Select value={currency} onValueChange={setCurrency}>
                    <SelectTrigger className="rounded-sm font-mono mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">Amount</Label>
                  <Input data-testid="inv-amount-input" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="1000" className="rounded-sm font-mono mt-1" />
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Due date (optional)</Label>
                <Input data-testid="inv-due-input" type="datetime-local" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="rounded-sm font-mono mt-1" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Memo</Label>
                <Input data-testid="inv-memo-input" value={memo} onChange={(e) => setMemo(e.target.value)} className="rounded-sm font-mono mt-1" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
              <Button onClick={onCreate} disabled={busy || !payerVpa || !amount} data-testid="inv-submit" className="rounded-sm">Issue</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex gap-2 mb-4">
        {[["any", "All"], ["issued", "Issued"], ["received", "Received"]].map(([k, l]) => (
          <button
            key={k}
            data-testid={`inv-tab-${k}`}
            onClick={() => setTab(k)}
            className={`px-3 py-1.5 text-xs uppercase tracking-widest border rounded-sm ${tab === k ? "border-foreground bg-foreground text-background" : "border-border hover:border-foreground"}`}
          >
            {l}
          </button>
        ))}
      </div>

      <div className="border border-border rounded-sm bg-card divide-y divide-border">
        {items.length === 0 && (
          <div className="p-6 text-center text-muted-foreground text-sm">No invoices.</div>
        )}
        {items.map((inv) => {
          const isIssuer = inv.issuer_business_id === business?.id;
          return (
            <Link
              key={inv.id}
              to={`/app/invoices/${inv.id}`}
              data-testid={`inv-row-${inv.id}`}
              className="grid grid-cols-12 gap-3 p-4 items-center hover:bg-muted/40 transition-colors"
            >
              <div className="col-span-2">
                <span className={`px-2 py-0.5 border rounded-sm text-[10px] uppercase tracking-widest ${STATUS_PILL[inv.status] || ""}`}>
                  {inv.status}
                </span>
              </div>
              <div className="col-span-3 truncate">
                <span className="text-muted-foreground">{isIssuer ? "to" : "from"}</span>{" "}
                <span className="font-medium">{isIssuer ? inv.payer_legal_name : inv.issuer_legal_name}</span>
              </div>
              <div className="col-span-2 font-mono text-sm">
                {fmt(inv.amount)} <span className="text-muted-foreground">{inv.currency}</span>
              </div>
              <div className="col-span-3 text-xs text-muted-foreground truncate">{inv.memo || "—"}</div>
              <div className="col-span-2 text-[11px] font-mono text-muted-foreground text-right">
                {inv.due_date ? `due ${new Date(inv.due_date).toLocaleDateString()}` : ""}
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
